import StarterKit from "@tiptap/starter-kit"
import { Table } from "@tiptap/extension-table"
import { TableCell } from "@tiptap/extension-table-cell"
import { TableHeader } from "@tiptap/extension-table-header"
import { TableRow } from "@tiptap/extension-table-row"
import Underline from "@tiptap/extension-underline"
import TextAlign from "@tiptap/extension-text-align"
import Placeholder from "@tiptap/extension-placeholder"
import { Mathematics } from "@tiptap/extension-mathematics"
import type { Extensions } from "@tiptap/core"

import { ResizableImage } from "./extensions/ResizableImage"
import { DiagramNode } from "./extensions/DiagramNode"
import { MathEditRequest } from "./types"

import "katex/contrib/mhchem"
import "prosemirror-tables/style/tables.css"

type CreateTiptapExtensionsOptions = {
  placeholder?: string
  onMathEditRequest?: (request: MathEditRequest) => void
  readOnly?: boolean
}

export const createTiptapExtensions = ({
  placeholder = "Start writing…",
  onMathEditRequest,
  readOnly = false,
}: CreateTiptapExtensionsOptions = {}): Extensions => {
  const mathematicsConfig: Parameters<typeof Mathematics.configure>[0] = {
    katexOptions: {
      throwOnError: false,
    },
  }

  if (onMathEditRequest) {
    mathematicsConfig.inlineOptions = {
      onClick: (node, pos) => {
        onMathEditRequest({
          mode: "edit-inline",
          latex: node.attrs.latex,
          pos,
        })
      },
    }
    mathematicsConfig.blockOptions = {
      onClick: (node, pos) => {
        onMathEditRequest({
          mode: "edit-block",
          latex: node.attrs.latex,
          pos,
        })
      },
    }
  }

  const extensions: Extensions = [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      link: {
        openOnClick: readOnly,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      },
    }),
    Underline,
    Table.configure({
      resizable: !readOnly,
      renderWrapper: true,
      HTMLAttributes: {
        class: "tiptap-editor__table",
      },
    }),
    TableRow,
    TableHeader.configure({
      HTMLAttributes: {
        class: "tiptap-editor__table-header-cell",
      },
    }),
    TableCell.configure({
      HTMLAttributes: {
        class: "tiptap-editor__table-cell",
      },
    }),
    TextAlign.configure({
      types: ["heading", "paragraph"],
    }),
    DiagramNode,
    ResizableImage.configure({
      inline: false,
      allowBase64: true,
      resize: {
        enabled: !readOnly,
        minWidth: 80,
        minHeight: 60,
        alwaysPreserveAspectRatio: true,
      },
    }),
    Mathematics.configure(mathematicsConfig),
  ]

  if (!readOnly) {
    extensions.push(
      Placeholder.configure({
        placeholder,
      })
    )
  }

  return extensions
}
